import React, { type FC } from 'react';
import { useIntl } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';

import Avatar, { AvatarContent } from '@atlaskit/avatar';
import { Inline } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import Link from '@atlaskit/link';
import Skeleton from '@atlaskit/skeleton';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { ConfluenceIcon } from '@atlaskit/logo';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper';
import { PEOPLE_DIRECTORY } from '@confluence/named-routes/entry-points/routes';

import { truncateName } from '../userUtils';

import { DetailRow } from './DetailRow';
import { InlineRetryError } from './InlineRetryError';

const MAX_DISPLAY_NAME_LENGTH = 25;
const PEOPLE_DIRECTORY_URL = PEOPLE_DIRECTORY.toUrl();

const noop = () => {};

type PersonRowProps = {
	loadingTestId: string;
	icon: React.JSX.Element;
	analyticsRole: string;
	i18n: {
		personLabel: MessageDescriptor;
		accessibleAvatarLabel: MessageDescriptor;
	};
	data: {
		accountId: string | null;
		displayName: string;
		profilePicturePath?: string;
	};
	error?: ApolloError;
	refetch: () => void;
	loading: boolean;
};

export const PersonRow: FC<PersonRowProps> = ({
	loadingTestId,
	icon,
	analyticsRole,
	i18n: { personLabel, accessibleAvatarLabel },
	data: { accountId, displayName, profilePicturePath },
	error,
	refetch,
	loading,
}) => {
	const { formatMessage } = useIntl();

	const renderPersonRow = () => {
		if (loading) {
			return (
				<UFOLoadHold name="person-row-details-panel-hold">
					<Skeleton width="80px" height="24px" borderRadius={3} testId={loadingTestId} />
				</UFOLoadHold>
			);
		}

		if (error) {
			return <InlineRetryError error={error} refetch={refetch} />;
		}

		const profileURL = accountId ? `${PEOPLE_DIRECTORY_URL}/${accountId}` : '';
		const truncatedName = truncateName(displayName, MAX_DISPLAY_NAME_LENGTH);

		return (
			<Inline alignBlock="center" alignInline="center" space="space.100">
				<Tooltip content={displayName}>
					{profileURL ? (
						<Link
							appearance="subtle"
							href={profileURL}
							analyticsContext={{
								source: 'contentWrapper',
								attributes: { role: analyticsRole, name: 'profileName' },
							}}
						>
							{truncatedName}
						</Link>
					) : (
						<span>{truncatedName}</span>
					)}
				</Tooltip>
				<ProfileCardWrapper userId={accountId}>
					{profilePicturePath === 'confluence-icon' ? (
						<Avatar
							size="small"
							href={profileURL}
							name={formatMessage(accessibleAvatarLabel, { displayName })}
							onClick={
								/* an analytics event is only fired if onClick is provided,
									but providing onClick will change behavior if href is not provided */
								profileURL ? noop : undefined
							}
							analyticsContext={{
								source: 'contentWrapper',
								attributes: { role: analyticsRole, name: 'profileImage' },
							}}
						>
							<AvatarContent>
								<ConfluenceIcon size="small" appearance="brand" />
							</AvatarContent>
						</Avatar>
					) : (
						<Avatar
							size="small"
							href={profileURL}
							src={profilePicturePath ?? undefined}
							name={formatMessage(accessibleAvatarLabel, { displayName })}
							onClick={
								/* an analytics event is only fired if onClick is provided,
									but providing onClick will change behavior if href is not provided */
								profileURL ? noop : undefined
							}
							analyticsContext={{
								source: 'contentWrapper',
								attributes: { role: analyticsRole, name: 'profileImage' },
							}}
						/>
					)}
				</ProfileCardWrapper>
			</Inline>
		);
	};

	return (
		<DetailRow itemName={formatMessage(personLabel)} icon={icon}>
			{renderPersonRow()}
		</DetailRow>
	);
};
